import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Progress } from '../../ui/progress';
import { Checkbox } from '../../ui/checkbox';
import { useToast } from '../../../hooks/use-toast';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import SolutionReviewModal from './SolutionReviewModal';
import { useTenantCategories } from './useTenantCategories';
import { normalizeBusinessRole } from '../../../lib/role';

// D17 — client role gate (defense-in-depth; backend authoritative). Consumes the canonical Web role
// normalizer (lib/role.js): administrator + group_administrator (+ variants) collapse to 'admin', so the
// semantic approver set is {admin, manager}.
import {
  Sparkles, Wand2, ClipboardCheck, ListChecks, LayoutGrid, HelpCircle, RefreshCw, Save,
  CheckCircle, Archive, X, Edit, AlertTriangle, Bot, User, Loader2, Lock, Play
} from 'lucide-react';

/**
 * AI Knowledge Builder (KB3, web Business plane only).
 *
 * Wired 100% to the REAL KB1/KB2 backend routes under /api/v1/solutions/knowledge-builder/*.
 * No mock generation, no fake success. Generated items are draft/suggested (internal only) until a
 * manager/admin explicitly approves them via the existing /solutions/{id}/approve route. Approved
 * knowledge then becomes live for AI Chat/Voice immediately (see Live AI Knowledge panel).
 *
 * Tenant: currentBusiness.brandId (business id) / currentBusiness.businessName (display).
 */

// The 12 categories KB2 guarantees coverage of (mirror of the backend REQUIRED_CATEGORIES).
const REQUIRED_CATEGORIES = [
  'Business Information', 'Products', 'Services', 'Pricing', 'Payments', 'Appointments',
  'Delivery', 'Returns', 'Support', 'Policies', 'Troubleshooting', 'General Questions',
];

const PAYMENT_OPTIONS = [
  'Cash', 'Debit', 'Credit', 'Bank transfer', 'Apple Pay', 'Google Pay', 'PayPal',
  'Mobile Money', 'Interac e-Transfer', 'Other',
];

// Intake field config. `key` MUST match the backend answer field_key. type: text|textarea|bool|multi.
// Bool is tri-state (unknown => skipped, never invented). Field keys used by KB2 validation:
// business_name, business_description, operating_hours, contact_methods, payment_methods, products,
// services, walk_ins_accepted, walk_in_policy, delivery_available, delivery_fees, delivery_timelines,
// appointment_required, booking_method, online_orders, ordering_method, return_policy, policies.
const INTAKE_SECTIONS = [
  { key: 'identity', title: 'Business Identity', fields: [
    { key: 'business_name', label: 'Business Name', type: 'text' },
    { key: 'address', label: 'Address', type: 'text' },
    { key: 'city', label: 'City', type: 'text' },
    { key: 'province', label: 'Province / State', type: 'text' },
    { key: 'country', label: 'Country', type: 'text' },
    { key: 'phone', label: 'Phone', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'website', label: 'Website', type: 'text' },
    { key: 'social_media', label: 'Social Media', type: 'text' },
  ]},
  { key: 'profile', title: 'Business Profile', fields: [
    { key: 'business_description', label: 'Business description', type: 'textarea' },
    { key: 'products', label: 'Products sold', type: 'textarea' },
    { key: 'services', label: 'Services provided', type: 'textarea' },
    { key: 'target_customers', label: 'Target customers', type: 'text' },
    { key: 'industries_served', label: 'Industries served', type: 'text' },
    { key: 'differentiator', label: 'Differentiator', type: 'text' },
    { key: 'mission_vision', label: 'Mission / vision', type: 'textarea' },
  ]},
  { key: 'operations', title: 'Operations', fields: [
    { key: 'opening_days', label: 'Opening days', type: 'text' },
    { key: 'operating_hours', label: 'Opening hours', type: 'text' },
    { key: 'holiday_hours', label: 'Holiday hours', type: 'text' },
    { key: 'appointment_required', label: 'Appointment required', type: 'bool' },
    { key: 'walk_ins_accepted', label: 'Walk-ins accepted', type: 'bool' },
    { key: 'delivery_available', label: 'Delivery available', type: 'bool' },
    { key: 'pickup_available', label: 'Pickup available', type: 'bool' },
    { key: 'online_orders', label: 'Online orders', type: 'bool' },
    { key: 'service_area', label: 'Service area', type: 'text' },
  ]},
  { key: 'payments', title: 'Payments', fields: [
    { key: 'payment_methods', label: 'Accepted payment methods', type: 'multi', options: PAYMENT_OPTIONS },
  ]},
  { key: 'delivery', title: 'Shipping / Delivery', fields: [
    { key: 'delivery_locations', label: 'Delivery locations', type: 'text' },
    { key: 'delivery_fees', label: 'Delivery fees', type: 'text' },
    { key: 'delivery_timelines', label: 'Delivery timelines', type: 'text' },
    { key: 'same_day_delivery', label: 'Same-day delivery', type: 'bool' },
    { key: 'international_shipping', label: 'International shipping', type: 'bool' },
  ]},
  { key: 'booking', title: 'Booking', fields: [
    { key: 'booking_method', label: 'How appointments are booked', type: 'text' },
    { key: 'cancellation_policy', label: 'Cancellation policy', type: 'textarea' },
    { key: 'rescheduling_policy', label: 'Rescheduling policy', type: 'textarea' },
    { key: 'late_arrival_policy', label: 'Late arrival policy', type: 'textarea' },
    { key: 'ordering_method', label: 'How online orders are placed', type: 'text' },
  ]},
  { key: 'returns', title: 'Returns / Refunds', fields: [
    { key: 'return_policy', label: 'Return policy', type: 'textarea' },
    { key: 'refund_policy', label: 'Refund policy', type: 'textarea' },
    { key: 'exchange_policy', label: 'Exchange policy', type: 'textarea' },
  ]},
  { key: 'support', title: 'Support', fields: [
    { key: 'contact_methods', label: 'Primary contact methods', type: 'text' },
    { key: 'support_phone', label: 'Support phone', type: 'text' },
    { key: 'support_whatsapp', label: 'WhatsApp', type: 'text' },
    { key: 'support_email', label: 'Support email', type: 'text' },
    { key: 'support_website', label: 'Support website', type: 'text' },
    { key: 'support_in_person', label: 'In-person support', type: 'bool' },
  ]},
  { key: 'policies', title: 'Policies', fields: [
    { key: 'privacy_policy', label: 'Privacy policy', type: 'textarea' },
    { key: 'warranty_policy', label: 'Warranty policy', type: 'textarea' },
    { key: 'terms_of_service', label: 'Terms of service', type: 'textarea' },
    { key: 'service_guarantees', label: 'Service guarantees', type: 'textarea' },
    { key: 'policies', label: 'General policies summary', type: 'textarea' },
  ]},
  { key: 'extras', title: 'Customer Questions & Notes', fields: [
    { key: 'common_questions', label: 'Common questions customers ask', type: 'textarea' },
    { key: 'special_instructions', label: 'What customers should always know', type: 'textarea' },
  ]},
];

const ALL_FIELDS = INTAKE_SECTIONS.flatMap((s) => s.fields);
const FIELD_TYPE = Object.fromEntries(ALL_FIELDS.map((f) => [f.key, f.type]));

const isEmptyVal = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);

const KnowledgeBuilder = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { makeAuthenticatedRequest, user } = useCRMAuth();
  const { currentBusiness } = useBusiness();
  const canApprove = ['admin', 'manager'].includes(normalizeBusinessRole(user?.role));
  const [reviewTarget, setReviewTarget] = useState(null);
  const businessId = currentBusiness?.brandId;
  // ONE category authority for draft authoring: the tenant's enabled Category Management set — the SAME
  // source the Review modal and the backend approval gate (TaxonomyService.resolve_effective_categories)
  // use. The Edit-draft picker must offer THESE, never the 12 generic coverage names (REQUIRED_CATEGORIES),
  // which remain only for Coverage/Gap analysis below.
  const { categories: tenantCategories, loaded: tenantCatsLoaded } = useTenantCategories(businessId, makeAuthenticatedRequest);
  const businessName = currentBusiness?.businessName;
  const storageKey = businessId ? `kb_session_${businessId}` : null;

  // Display-only localization for the module-level intake config. The stored field_key / option
  // VALUES stay English (they are the backend answer contract); only the visible label is localized.
  const PAY_LABEL_KEY = {
    'Cash': 'sb.kb.pay.cash', 'Debit': 'sb.kb.pay.debit', 'Credit': 'sb.kb.pay.credit',
    'Bank transfer': 'sb.kb.pay.bankTransfer', 'Apple Pay': 'sb.kb.pay.applePay', 'Google Pay': 'sb.kb.pay.googlePay',
    'PayPal': 'sb.kb.pay.payPal', 'Mobile Money': 'sb.kb.pay.mobileMoney', 'Interac e-Transfer': 'sb.kb.pay.interac',
    'Other': 'sb.kb.pay.other',
  };
  const payLabel = (opt) => (PAY_LABEL_KEY[opt] ? t(PAY_LABEL_KEY[opt]) : opt);
  const sectionTitle = (sec) => t(`sb.kb.sec.${sec.key}`);
  const fieldLabel = (f) => t(`sb.kb.f.${f.key}`);

  const [sessionId, setSessionId] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [step, setStep] = useState('intake');

  const [form, setForm] = useState({});
  const snapshotRef = React.useRef({}); // last-saved values, to POST only real changes
  const [saving, setSaving] = useState(false);

  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);

  const [generated, setGenerated] = useState([]);   // draft/suggested for this session
  const [approvedItems, setApprovedItems] = useState([]); // approved for this session
  const [archivedItems, setArchivedItems] = useState([]);
  const [loadingReview, setLoadingReview] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const [gaps, setGaps] = useState([]);
  const [gapsError, setGapsError] = useState(null);

  const [editItem, setEditItem] = useState(null);
  const [gapDraft, setGapDraft] = useState(null); // { gap, answer, category }

  // ---- low-level request helper: returns parsed json, throws Error(detail) on !ok ----
  const req = useCallback(async (path, options) => {
    const resp = await makeAuthenticatedRequest(path, options);
    if (!resp.ok) {
      const e = await resp.json().catch(() => ({}));
      const err = new Error(e.detail || `HTTP ${resp.status}`);
      err.status = resp.status;
      throw err;
    }
    return resp.status === 204 ? {} : resp.json();
  }, [makeAuthenticatedRequest]);

  // ---- bootstrap: resume a persisted session (validate it still exists) or wait for Start ----
  const hydrateForm = useCallback((answers) => {
    const next = {};
    (answers || []).forEach((a) => {
      if (a.is_missing) return;
      next[a.field_key] = a.value;
    });
    setForm(next);
    snapshotRef.current = { ...next };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!businessId) { setBootstrapping(false); return; }
      setBootstrapping(true);
      const saved = storageKey ? localStorage.getItem(storageKey) : null;
      if (!saved) { setBootstrapping(false); return; }
      try {
        await req(`/api/v1/solutions/knowledge-builder/session/${saved}`); // 404 if gone
        const ans = await req(`/api/v1/solutions/knowledge-builder/session/${saved}/answers`);
        if (cancelled) return;
        setSessionId(saved);
        hydrateForm(ans.answers);
      } catch (e) {
        if (storageKey) localStorage.removeItem(storageKey); // stale/foreign session — drop it
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessId, storageKey, req, hydrateForm]);

  const startSession = async () => {
    if (!businessId) {
      toast({ title: t('sb.kb.toast.noBusiness'), description: t('sb.kb.toast.noBusinessDesc'), variant: 'destructive' });
      return;
    }
    try {
      const data = await req('/api/v1/solutions/knowledge-builder/session', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ business_id: businessId, brand_id: currentBusiness?.brandId }),
      });
      setSessionId(data.session_id);
      if (storageKey) localStorage.setItem(storageKey, data.session_id);
      setForm({}); snapshotRef.current = {}; setValidation(null); setGenResult(null);
      toast({ title: t('sb.kb.toast.started'), description: t('sb.kb.toast.startedDesc') });
    } catch (e) {
      toast({ title: t('sb.kb.toast.couldNotStart'), description: e.message, variant: 'destructive' });
    }
  };

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  // Save only changed fields. Emptied-but-previously-set fields are recorded is_missing (never invented).
  const saveProgress = async () => {
    if (!sessionId) return;
    setSaving(true);
    let posted = 0;
    try {
      for (const f of ALL_FIELDS) {
        const cur = form[f.key];
        const prev = snapshotRef.current[f.key];
        const same = JSON.stringify(cur ?? null) === JSON.stringify(prev ?? null);
        if (same) continue;
        if (isEmptyVal(cur)) {
          if (!isEmptyVal(prev)) {
            await req('/api/v1/solutions/knowledge-builder/answer', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ session_id: sessionId, field_key: f.key, is_missing: true }),
            });
            posted++;
          }
          continue;
        }
        await req('/api/v1/solutions/knowledge-builder/answer', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId, field_key: f.key, value: cur }),
        });
        posted++;
      }
      snapshotRef.current = { ...form };
      toast({ title: t('sb.kb.toast.progressSaved'), description: posted ? t('sb.kb.toast.savedFields', { count: posted }) : t('sb.kb.toast.nothingToSave') });
    } catch (e) {
      toast({ title: t('sb.kb.toast.saveFailed'), description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const runValidation = async () => {
    if (!sessionId) return;
    setValidating(true);
    try {
      await saveProgress();
      const data = await req(`/api/v1/solutions/knowledge-builder/session/${sessionId}/validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
      });
      setValidation(data.validation);
      setStep('validate');
    } catch (e) {
      toast({ title: t('sb.kb.toast.validationFailed'), description: e.message, variant: 'destructive' });
    } finally {
      setValidating(false);
    }
  };

  const runGeneration = async () => {
    if (!sessionId) return;
    setGenerating(true);
    setGenResult(null);
    try {
      await saveProgress();
      const data = await req(`/api/v1/solutions/knowledge-builder/session/${sessionId}/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      setGenResult(data);
      toast({ title: t('sb.kb.toast.draftsGenerated'), description: t('sb.kb.toast.draftsGeneratedDesc', { count: data.generated }) });
      await loadReview();
      setStep('review');
    } catch (e) {
      // Honest failure — no fake success, no mock fallback. 503 = provider unavailable.
      const msg = e.status === 503
        ? t('sb.kb.toast.providerUnavailable')
        : e.message;
      toast({ title: t('sb.kb.toast.generationFailed'), description: msg, variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const loadReview = useCallback(async () => {
    if (!sessionId || !businessId) return;
    setLoadingReview(true);
    try {
      const gen = await req(`/api/v1/solutions/knowledge-builder/session/${sessionId}/generated`);
      setGenerated(gen.generated || []);
      // approved + archived for THIS session (derived from the tenant solutions list, filtered by session).
      const appr = await req(`/api/v1/solutions/${businessId}?status=approved`);
      setApprovedItems((appr.solutions || []).filter((s) => s.builder_session_id === sessionId));
      try {
        const arch = await req(`/api/v1/solutions/${businessId}?status=archived`);
        setArchivedItems((arch.solutions || []).filter((s) => s.builder_session_id === sessionId));
      } catch { setArchivedItems([]); }
    } catch (e) {
      toast({ title: t('sb.kb.toast.loadGenFailed'), description: e.message, variant: 'destructive' });
    } finally {
      setLoadingReview(false);
    }
  }, [sessionId, businessId, req, toast, t]);

  const loadGaps = useCallback(async () => {
    if (!businessId) return;
    setGapsError(null);
    try {
      const data = await req(`/api/v1/solutions/knowledge-builder/gaps?business_id=${encodeURIComponent(businessId)}`);
      setGaps(data.gaps || []);
    } catch (e) {
      setGaps([]);
      setGapsError(e.message);
    }
  }, [businessId, req]);

  useEffect(() => { if (step === 'review' && sessionId) loadReview(); }, [step, sessionId, loadReview]);
  useEffect(() => { if (step === 'gaps') loadGaps(); }, [step, loadGaps]);

  // ---- governance actions on individual items (existing real routes) ----
  // D17 — direct one-click approve removed. Approval now flows through the canonical SolutionReviewModal
  // (full review + sanitization + duplicate decision), which the backend re-validates.
  const rejectItem = async (id) => {
    try {
      await req(`/api/v1/solutions/${id}/reject`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      toast({ title: t('sb.kb.toast.rejected'), description: t('sb.kb.toast.rejectedDesc') });
      loadReview();
    } catch (e) { toast({ title: t('sb.kb.toast.rejectFailed'), description: e.message, variant: 'destructive' }); }
  };
  const archiveItem = async (id) => {
    try {
      await req(`/api/v1/solutions/${id}/archive`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      toast({ title: t('sb.kb.toast.archived'), description: t('sb.kb.toast.archivedDesc') });
      loadReview();
    } catch (e) { toast({ title: t('sb.kb.toast.archiveFailed'), description: e.message, variant: 'destructive' }); }
  };
  const saveEdit = async () => {
    if (!editItem) return;
    try {
      await req(`/api/v1/solutions/${editItem.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editItem.title, description: editItem.title,
          resolution: editItem.resolution,
          // Keep the structured steps in sync with a quick single-answer edit, so the Review modal
          // repopulates consistently (one newline-separated step per line).
          resolution_steps: (editItem.resolution || '').split(/\n+/).map((s) => s.trim()).filter(Boolean),
          category: editItem.category,
          keywords: editItem.keywordsStr ? editItem.keywordsStr.split(',').map((k) => k.trim()).filter(Boolean) : (editItem.keywords || []),
        }),
      });
      toast({ title: t('sb.kb.toast.draftUpdated'), description: t('sb.kb.toast.draftUpdatedDesc') });
      setEditItem(null);
      loadReview();
    } catch (e) { toast({ title: t('sb.kb.toast.updateFailed'), description: e.message, variant: 'destructive' }); }
  };
  const createDraftFromGap = async () => {
    if (!gapDraft) return;
    try {
      await req(`/api/v1/solutions/knowledge-builder/gaps/${gapDraft.gap.id}/draft-faq`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer: gapDraft.answer, category: gapDraft.category }),
      });
      toast({ title: t('sb.kb.toast.draftCreated'), description: t('sb.kb.toast.draftCreatedDesc') });
      setGapDraft(null);
      loadGaps();
    } catch (e) { toast({ title: t('sb.kb.toast.couldNotCreate'), description: e.message, variant: 'destructive' }); }
  };

  // ---- coverage (derived from real generated + approved data) ----
  const coverage = useMemo(() => {
    const internal = generated;
    const all = [...internal, ...approvedItems];
    const byCat = {};
    REQUIRED_CATEGORIES.forEach((c) => { byCat[c] = 0; });
    all.forEach((s) => { if (byCat[s.category] !== undefined) byCat[s.category] += 1; });
    const missing = REQUIRED_CATEGORIES.filter((c) => byCat[c] === 0);
    return {
      total: all.length,
      approved: approvedItems.length,
      internal: internal.length,
      byCat, missing,
      readiness: validation?.readiness_score ?? null,
    };
  }, [generated, approvedItems, validation]);

  // ---------------------------------------------------------------- render helpers
  const renderField = (f) => {
    const val = form[f.key];
    if (f.type === 'textarea') {
      return <Textarea rows={3} value={val || ''} onChange={(e) => setField(f.key, e.target.value)} placeholder={t('sb.kb.blankUnknownTextarea')} />;
    }
    if (f.type === 'bool') {
      return (
        <Select value={val || 'unknown'} onValueChange={(v) => setField(f.key, v === 'unknown' ? '' : v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="unknown">{t('sb.kb.unknownSkip')}</SelectItem>
            <SelectItem value="yes">{t('common.yes')}</SelectItem>
            <SelectItem value="no">{t('common.no')}</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    if (f.type === 'multi') {
      const arr = Array.isArray(val) ? val : [];
      return (
        <div className="grid grid-cols-2 gap-2">
          {f.options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm">
              <Checkbox checked={arr.includes(opt)} onCheckedChange={(ck) => {
                const next = ck ? [...arr, opt] : arr.filter((x) => x !== opt);
                setField(f.key, next);
              }} />
              {payLabel(opt)}
            </label>
          ))}
        </div>
      );
    }
    return <Input value={val || ''} onChange={(e) => setField(f.key, e.target.value)} placeholder={t('sb.kb.blankUnknown')} />;
  };

  const statusBadge = (s) => {
    const map = {
      draft: { c: 'bg-gray-100 text-gray-700', t: t('sb.kb.status.draft') },
      suggested: { c: 'bg-amber-100 text-amber-800', t: t('sb.kb.status.suggested') },
      pending_approval: { c: 'bg-blue-100 text-blue-800', t: t('sb.kb.status.pending') },
      approved: { c: 'bg-green-100 text-green-800', t: t('sb.kb.status.approved') },
      archived: { c: 'bg-slate-200 text-slate-700', t: t('sb.kb.status.archived') },
      rejected: { c: 'bg-red-100 text-red-700', t: t('sb.kb.status.rejected') },
    };
    const m = map[s] || { c: 'bg-gray-100 text-gray-700', t: s };
    return <Badge className={m.c}>{m.t}</Badge>;
  };
  const sourceBadge = (src) => src === 'owner_supplied'
    ? <Badge variant="outline" className="text-green-700 border-green-300"><User className="h-3 w-3 mr-1" />{t('sb.kb.ownerSupplied')}</Badge>
    : <Badge variant="outline" className="text-blue-700 border-blue-300"><Bot className="h-3 w-3 mr-1" />{t('sb.kb.aiSuggested')}</Badge>;

  const ItemCard = ({ s, actions = true }) => (
    <Card className="bg-white border shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-gray-900">{s.title}</div>
            <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{s.resolution}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Badge variant="secondary" className="bg-purple-50 text-purple-700">{s.category}</Badge>
              {statusBadge(s.status)}
              {sourceBadge(s.source)}
              {typeof s.confidence_score === 'number' && <Badge variant="outline" className="text-xs">conf {(s.confidence_score * 100).toFixed(0)}%</Badge>}
              {s.difficulty && <Badge variant="outline" className="text-xs">{s.difficulty}</Badge>}
              {(s.keywords || []).slice(0, 4).map((k, i) => <Badge key={i} variant="outline" className="text-xs">{k}</Badge>)}
            </div>
          </div>
          {actions && (
            <div className="flex flex-col gap-2 flex-shrink-0">
              {s.status !== 'approved' && canApprove && (
                <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700" onClick={() => setReviewTarget(s)}>
                  <CheckCircle className="h-4 w-4 mr-1" />Review
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setEditItem({ ...s, keywordsStr: (s.keywords || []).join(', ') })}>
                <Edit className="h-4 w-4 mr-1" />{t('common.edit')}
              </Button>
              {s.status !== 'archived' ? (
                <Button size="sm" variant="outline" className="text-slate-600" onClick={() => archiveItem(s.id)}>
                  <Archive className="h-4 w-4 mr-1" />{t('common.archive')}
                </Button>
              ) : null}
              {s.status !== 'approved' && s.status !== 'rejected' && (
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => rejectItem(s.id)}>
                  <X className="h-4 w-4 mr-1" />{t('common.reject')}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const groupedForReview = useMemo(() => {
    const list = [...generated, ...approvedItems, ...(showArchived ? archivedItems : [])];
    const byCat = {};
    list.forEach((s) => { (byCat[s.category] = byCat[s.category] || []).push(s); });
    return byCat;
  }, [generated, approvedItems, archivedItems, showArchived]);

  // ---------------------------------------------------------------- top-level guards
  if (!businessId) {
    return (
      <Card className="border-amber-200"><CardContent className="p-8 text-center">
        <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <p className="text-gray-700 font-medium">{t('sb.kb.noBusinessContext')}</p>
        <p className="text-sm text-gray-500 mt-1">{t('sb.kb.selectBusiness')}</p>
      </CardContent></Card>
    );
  }
  if (bootstrapping) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Sparkles className="h-5 w-5 text-purple-600" />{t('sb.kb.title')}</h2>
          <p className="text-sm text-gray-600">
            {t('sb.kb.subtitle')}
            {businessName ? ` · ${businessName}` : ''}
          </p>
        </div>
        {sessionId && (
          <Button variant="outline" size="sm" onClick={startSession}><Play className="h-4 w-4 mr-2" />{t('sb.kb.newSession')}</Button>
        )}
      </div>

      {!sessionId ? (
        <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 shadow-lg">
          <CardContent className="p-10 text-center">
            <Wand2 className="h-12 w-12 text-purple-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('sb.kb.buildTitle')}</h3>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              {t('sb.kb.buildBlurb')}
            </p>
            <Button className="bg-gradient-to-r from-purple-600 to-blue-600" onClick={startSession}>
              <Play className="h-4 w-4 mr-2" />{t('sb.kb.start')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={step} onValueChange={setStep}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="intake"><ClipboardCheck className="h-4 w-4 mr-1" />{t('sb.kb.tab.intake')}</TabsTrigger>
            <TabsTrigger value="validate"><ListChecks className="h-4 w-4 mr-1" />{t('sb.kb.tab.validate')}</TabsTrigger>
            <TabsTrigger value="review"><Wand2 className="h-4 w-4 mr-1" />{t('sb.kb.tab.review')}</TabsTrigger>
            <TabsTrigger value="coverage"><LayoutGrid className="h-4 w-4 mr-1" />{t('sb.kb.tab.coverage')}</TabsTrigger>
            <TabsTrigger value="gaps"><HelpCircle className="h-4 w-4 mr-1" />{t('sb.kb.tab.gaps')}</TabsTrigger>
          </TabsList>

          {/* INTAKE */}
          <TabsContent value="intake" className="space-y-4 mt-4">
            <div className="flex items-center justify-between sticky top-0 z-10 bg-white/80 backdrop-blur py-2">
              <p className="text-sm text-gray-500">{t('sb.kb.skipHint')}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={saveProgress} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}{t('sb.kb.saveProgress')}
                </Button>
                <Button size="sm" onClick={runValidation} disabled={validating}>
                  {validating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ListChecks className="h-4 w-4 mr-2" />}{t('common.validate')}
                </Button>
              </div>
            </div>
            {INTAKE_SECTIONS.map((sec) => (
              <Card key={sec.key} className="bg-white/70 border-0 shadow">
                <CardHeader><CardTitle className="text-base">{sectionTitle(sec)}</CardTitle></CardHeader>
                <CardContent className="grid md:grid-cols-2 gap-4">
                  {sec.fields.map((f) => (
                    <div key={f.key} className={f.type === 'textarea' || f.type === 'multi' ? 'md:col-span-2 space-y-1.5' : 'space-y-1.5'}>
                      <Label>{fieldLabel(f)}</Label>
                      {renderField(f)}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* VALIDATE */}
          <TabsContent value="validate" className="space-y-4 mt-4">
            <div className="flex justify-end"><Button size="sm" variant="outline" onClick={runValidation} disabled={validating}>
              <RefreshCw className={`h-4 w-4 mr-2 ${validating ? 'animate-spin' : ''}`} />{t('sb.kb.revalidate')}</Button></div>
            {!validation ? (
              <Card><CardContent className="p-8 text-center text-gray-500">{t('sb.kb.runValidationHint')}</CardContent></Card>
            ) : (
              <>
                <Card className="border-0 shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-800">{t('sb.kb.readiness')}</span>
                      <span className="text-sm text-gray-600">{Math.round((validation.readiness_score || 0) * 100)}%</span>
                    </div>
                    <Progress value={Math.round((validation.readiness_score || 0) * 100)} />
                    <p className="text-xs text-gray-500 mt-3">
                      {t('sb.kb.readinessHint')}
                    </p>
                  </CardContent>
                </Card>
                <div className="grid md:grid-cols-2 gap-4">
                  <ValBlock title={t('sb.kb.val.missingFields')} tone="amber" items={validation.missing_fields} empty={t('sb.kb.val.missingFieldsEmpty')} />
                  <ValBlock title={t('sb.kb.val.contradictions')} tone="red" items={validation.contradictions} empty={t('sb.kb.val.contradictionsEmpty')} />
                  <ValBlock title={t('sb.kb.val.vague')} tone="amber" items={validation.vague_answers} empty={t('sb.kb.val.vagueEmpty')} />
                  <ValBlock title={t('sb.kb.val.followups')} tone="blue" items={validation.follow_up_questions} empty={t('sb.kb.val.followupsEmpty')} />
                </div>
                <div className="flex items-center justify-between">
                  <Button variant="outline" onClick={() => setStep('intake')}>{t('sb.kb.completeMore')}</Button>
                  <Button className="bg-gradient-to-r from-purple-600 to-blue-600" onClick={() => setStep('review')}>
                    {t('sb.kb.continueGenerate')}
                  </Button>
                </div>
              </>
            )}
          </TabsContent>

          {/* GENERATE & REVIEW */}
          <TabsContent value="review" className="space-y-4 mt-4">
            <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
              <CardContent className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{t('sb.kb.generate50')}</h3>
                  <p className="text-sm text-gray-600">{t('sb.kb.generate50Sub')}</p>
                  {genResult && (
                    <p className="text-xs text-gray-600 mt-1">
                      Last run: {genResult.generated} items · batch {genResult.batch_status}
                      {genResult.counts ? ` · ${genResult.counts.owner_supplied} owner-supplied / ${genResult.counts.ai_suggested} AI-suggested` : ''}
                      {genResult.replaced_prior_drafts ? ` · replaced ${genResult.replaced_prior_drafts} prior drafts` : ''}
                    </p>
                  )}
                </div>
                <Button className="bg-gradient-to-r from-purple-600 to-blue-600" onClick={runGeneration} disabled={generating}>
                  {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('sb.kb.generating')}</> : <><Wand2 className="h-4 w-4 mr-2" />{t('sb.kb.generate50')}</>}
                </Button>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <Lock className="h-4 w-4 inline mr-1 text-gray-400" />
                {t('sb.kb.internalVsLive')}
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <Checkbox checked={showArchived} onCheckedChange={(v) => setShowArchived(!!v)} />{t('sb.kb.showArchived')}
                </label>
                <Button size="sm" variant="outline" onClick={loadReview} disabled={loadingReview}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingReview ? 'animate-spin' : ''}`} />{t('common.refresh')}
                </Button>
              </div>
            </div>

            {loadingReview ? (
              <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div>
            ) : (generated.length + approvedItems.length + (showArchived ? archivedItems.length : 0)) === 0 ? (
              <Card><CardContent className="p-10 text-center text-gray-500">
                <Bot className="h-10 w-10 mx-auto mb-3 text-gray-400" />
                {t('sb.kb.noGeneratedYet')}
              </CardContent></Card>
            ) : (
              Object.keys(groupedForReview).sort().map((cat) => (
                <div key={cat} className="space-y-2">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">{cat}
                    <Badge variant="outline" className="text-xs">{groupedForReview[cat].length}</Badge></h4>
                  {groupedForReview[cat].map((s) => <ItemCard key={s.id} s={s} />)}
                </div>
              ))
            )}
          </TabsContent>

          {/* COVERAGE */}
          <TabsContent value="coverage" className="space-y-4 mt-4">
            <div className="flex justify-end"><Button size="sm" variant="outline" onClick={loadReview} disabled={loadingReview}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingReview ? 'animate-spin' : ''}`} />{t('common.refresh')}</Button></div>
            <div className="grid md:grid-cols-4 gap-4">
              <MiniStat label={t('sb.kb.totalGenerated')} value={coverage.total} />
              <MiniStat label={t('sb.kb.customerFacing')} value={coverage.approved} accent="green" />
              <MiniStat label={t('sb.kb.internalDraft')} value={coverage.internal} accent="amber" />
              <MiniStat label={t('sb.kb.readiness')} value={coverage.readiness === null ? '—' : `${Math.round(coverage.readiness * 100)}%`} />
            </div>
            <Card className="border-0 shadow">
              <CardHeader><CardTitle className="text-base">{t('sb.kb.categoryCoverage')}</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-2">
                {REQUIRED_CATEGORIES.map((c) => (
                  <div key={c} className="flex items-center justify-between p-2 rounded bg-gray-50">
                    <span className="text-sm">{c}</span>
                    {coverage.byCat[c] > 0
                      ? <Badge className="bg-green-100 text-green-700">{coverage.byCat[c]}</Badge>
                      : <Badge className="bg-red-100 text-red-700">{t('sb.kb.missing')}</Badge>}
                  </div>
                ))}
              </CardContent>
            </Card>
            {coverage.missing.length > 0 && (
              <Card className="border-amber-200"><CardContent className="p-4 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                {t('sb.kb.missingCategories', { cats: coverage.missing.join(', ') })}
              </CardContent></Card>
            )}
          </TabsContent>

          {/* GAPS */}
          <TabsContent value="gaps" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">{t('sb.kb.gapsIntro')}</p>
              <Button size="sm" variant="outline" onClick={loadGaps}><RefreshCw className="h-4 w-4 mr-2" />{t('common.refresh')}</Button>
            </div>
            {gapsError ? (
              <Card className="border-amber-200"><CardContent className="p-6 text-center text-sm text-amber-800">
                <AlertTriangle className="h-5 w-5 mx-auto mb-2" />{t('sb.kb.gapsLoadError', { error: gapsError })}
              </CardContent></Card>
            ) : gaps.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-gray-500">{t('sb.kb.noGaps')}</CardContent></Card>
            ) : (
              gaps.map((g) => (
                <Card key={g.id} className="bg-white border shadow-sm"><CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{g.normalized_question}</div>
                    <div className="text-xs text-gray-500 mt-1">{t('sb.kb.seenTimes', { count: g.occurrences || 1, status: g.status })}</div>
                  </div>
                  {g.status === 'open' && (
                    <Button size="sm" variant="outline" onClick={() => setGapDraft({ gap: g, answer: '', category: '' })}>
                      <Wand2 className="h-4 w-4 mr-1" />{t('sb.kb.createDraftFaq')}
                    </Button>
                  )}
                </CardContent></Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* EDIT DIALOG */}
      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t('sb.kb.editDraftFaq')}</DialogTitle></DialogHeader>
          {editItem && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>{t('sb.kb.question')}</Label>
                <Input value={editItem.title} onChange={(e) => setEditItem({ ...editItem, title: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t('sb.kb.answer')}</Label>
                <Textarea rows={4} value={editItem.resolution} onChange={(e) => setEditItem({ ...editItem, resolution: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t('sb.category')}</Label>
                  {tenantCatsLoaded && tenantCategories.length === 0 ? (
                    <p className="text-xs text-amber-700 border rounded px-3 py-2 bg-amber-50">
                      {t('sb.kb.noCategories') !== 'sb.kb.noCategories' ? t('sb.kb.noCategories')
                        : 'No categories are configured for this business. Add one in Category Management.'}
                    </p>
                  ) : (
                    <Select value={editItem.category} onValueChange={(v) => setEditItem({ ...editItem, category: v })} disabled={!tenantCatsLoaded}>
                      <SelectTrigger><SelectValue placeholder={tenantCatsLoaded ? 'Select a category…' : 'Loading categories…'} /></SelectTrigger>
                      <SelectContent>{tenantCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  )}</div>
                <div className="space-y-1.5"><Label>{t('sb.kb.keywords')}</Label>
                  <Input value={editItem.keywordsStr} onChange={(e) => setEditItem({ ...editItem, keywordsStr: e.target.value })} /></div>
              </div>
              <p className="text-xs text-gray-500"><Lock className="h-3 w-3 inline mr-1" />{t('sb.kb.editInternalHint')}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>{t('common.cancel')}</Button>
            <Button onClick={saveEdit}><Save className="h-4 w-4 mr-2" />{t('sb.kb.saveDraft')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GAP → DRAFT DIALOG */}
      <Dialog open={!!gapDraft} onOpenChange={(o) => !o && setGapDraft(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{t('sb.kb.createDraftFromGap')}</DialogTitle></DialogHeader>
          {gapDraft && (
            <div className="space-y-3">
              <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{gapDraft.gap.normalized_question}</div>
              <div className="space-y-1.5"><Label>{t('sb.kb.answer')}</Label>
                <Textarea rows={4} value={gapDraft.answer} onChange={(e) => setGapDraft({ ...gapDraft, answer: e.target.value })}
                  placeholder={t('sb.kb.answerPlaceholder')} /></div>
              <div className="space-y-1.5"><Label>{t('sb.category')}</Label>
                {tenantCatsLoaded && tenantCategories.length === 0 ? (
                  <p className="text-xs text-amber-700 border rounded px-3 py-2 bg-amber-50">
                    {t('sb.kb.noCategories') !== 'sb.kb.noCategories' ? t('sb.kb.noCategories')
                      : 'No categories are configured for this business. Add one in Category Management.'}
                  </p>
                ) : (
                  <Select value={gapDraft.category} onValueChange={(v) => setGapDraft({ ...gapDraft, category: v })} disabled={!tenantCatsLoaded}>
                    <SelectTrigger><SelectValue placeholder={tenantCatsLoaded ? 'Select a category…' : 'Loading categories…'} /></SelectTrigger>
                    <SelectContent>{tenantCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                )}</div>
              <p className="text-xs text-gray-500"><Lock className="h-3 w-3 inline mr-1" />{t('sb.kb.createdDraftHint')}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGapDraft(null)}>{t('common.cancel')}</Button>
            <Button onClick={createDraftFromGap} disabled={!gapDraft?.answer?.trim() || !tenantCategories.includes(gapDraft?.category)}><Wand2 className="h-4 w-4 mr-2" />{t('sb.kb.createDraft')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {reviewTarget && canApprove && (
        <SolutionReviewModal
          solution={reviewTarget}
          businessId={businessId}
          makeAuthenticatedRequest={makeAuthenticatedRequest}
          toast={toast}
          t={t}
          onClose={() => setReviewTarget(null)}
          onDone={() => { setReviewTarget(null); loadReview(); }}
        />
      )}
    </div>
  );
};

const ValBlock = ({ title, items, empty, tone }) => {
  const tones = { amber: 'border-amber-200 text-amber-800', red: 'border-red-200 text-red-800', blue: 'border-blue-200 text-blue-800' };
  const list = Array.isArray(items) ? items : [];
  return (
    <Card className={`border ${tones[tone] || ''}`}>
      <CardHeader className="pb-2"><CardTitle className="text-sm">{title} ({list.length})</CardTitle></CardHeader>
      <CardContent>
        {list.length === 0 ? <p className="text-sm text-gray-500">{empty}</p> : (
          <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">{list.map((x, i) => <li key={i}>{x}</li>)}</ul>
        )}
      </CardContent>
    </Card>
  );
};

const MiniStat = ({ label, value, accent }) => {
  const c = accent === 'green' ? 'text-green-600' : accent === 'amber' ? 'text-amber-600' : 'text-gray-900';
  return (
    <Card className="bg-white/70 border-0 shadow"><CardContent className="p-4">
      <p className="text-sm text-gray-600">{label}</p>
      <p className={`text-2xl font-bold ${c}`}>{value}</p>
    </CardContent></Card>
  );
};

export default KnowledgeBuilder;
